import { type ReactNode, useState } from 'react'

import { Icon, SecondaryButton, Tooltip } from '@joor/design-system'
import styled from 'styled-components'

import { ViewModeTaxVerificationIds } from '../retailerProfilePage.ids'
import type { RetailerProfile } from '../retailerProfilePage.types'
import { TaxIdVerificationModal } from './TaxIdVerificationModal'

const GET_VERIFIED_INFO =
  'Providing your Tax ID verifies your active business status, boosting your credibility with brands.'

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const Actions = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`

const VerifiedBadge = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
  cursor: default;
  flex-shrink: 0;
  line-height: 0;
`

function TaxVerifiedBadgeButton({
  tooltipContent,
  retailerName,
}: {
  tooltipContent: string
  retailerName: string
}) {
  return (
    <Tooltip content={tooltipContent} defaultPlacement="bottom">
      <VerifiedBadge
        type="button"
        id={ViewModeTaxVerificationIds.VerifiedBadge}
        aria-label={`Verified retailer: ${retailerName}`}
      >
        <Icon
          iconName="check-circle-on"
          iconSize="medium"
          type="default"
          variant="DEFAULT"
          iconColor="secondary-800"
        />
      </VerifiedBadge>
    </Tooltip>
  )
}

type Props = {
  canManageVerification: boolean
  showVerifiedBadgeForViewers: boolean
  retailerName: string
  profileTaxId?: string | null
  retailerProfile: RetailerProfile
  onProfileUpdated?: () => void
  children: ReactNode
}

export function TaxIdVerificationHeaderTrigger({
  canManageVerification,
  showVerifiedBadgeForViewers,
  retailerName,
  profileTaxId,
  retailerProfile,
  onProfileUpdated,
  children,
}: Props) {
  const [modalOpen, setModalOpen] = useState(false)
  const [isVerified, setIsVerified] = useState(false)

  const savedTaxId = profileTaxId?.trim() ?? ''
  const hasSavedTaxId = Boolean(savedTaxId)
  const showVerifiedBadge =
    showVerifiedBadgeForViewers || hasSavedTaxId || isVerified
  const badgeTooltipContent = hasSavedTaxId
    ? `Tax ID: ${savedTaxId}`
    : 'Your Tax ID is verified. Brands can see your verified status on your profile.'

  const showAnyTaxUi = canManageVerification || showVerifiedBadgeForViewers

  if (!showAnyTaxUi) {
    return <>{children}</>
  }

  return (
    <Row>
      {children}
      {showVerifiedBadge ? (
        <TaxVerifiedBadgeButton
          tooltipContent={badgeTooltipContent}
          retailerName={retailerName}
        />
      ) : (
        <>
          <Actions>
            <SecondaryButton
              id={ViewModeTaxVerificationIds.GetVerifiedButton}
              iconName="checkmark"
              onClick={() => setModalOpen(true)}
            >
              Get Verified
            </SecondaryButton>
            <Tooltip
              content={GET_VERIFIED_INFO}
              defaultPlacement="bottom"
              customTooltipStyles={{ maxWidth: 'min(320px, 90vw)' }}
            >
              <span
                id={ViewModeTaxVerificationIds.GetVerifiedInfoTrigger}
                aria-label="Why verify your Tax ID"
              >
                <Icon iconName="information" iconSize="small" />
              </span>
            </Tooltip>
          </Actions>
          <TaxIdVerificationModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            onTaxSaveSuccess={(hasTaxId) => setIsVerified(hasTaxId)}
            retailerProfile={retailerProfile}
            onProfileUpdated={onProfileUpdated}
          />
        </>
      )}
    </Row>
  )
}
